import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { getAuth } from 'firebase/auth';
import PDFPreviewModal from '../../pitch-builder-live/sections/PitchDeck/components/PDFPreviewModal';

// Import components
import ViewerForm from './ViewerForm';
import CompanyHeader from './CompanyHeader';
import TeamSection from './TeamSection';
import PitchDeckSection from './PitchDeckSection';
import FinancialsSection from './FinancialsSection';
import DueDiligenceSection from './DueDiligenceSection';
import ProductDemoSection from './ProductDemoSection';
import ContactSection from './ContactSection';
import Footer from './Footer';
import DocumentPreviewModal from './DocumentPreviewModal';

const LivePreview = () => {
  const { handle } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTeam, setActiveTeam] = useState(0);
  const [activeDoc, setActiveDoc] = useState(0);
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [analytics, setAnalytics] = useState({
    sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    totalTimeOnScreen: 0,
    pdfViewTime: 0,
    previewCount: 0,
    slidesViewed: [],
    slideViewTimes: {},
    slideViewDetails: {},
    totalSlideViews: 0,
    uniqueSlidesViewed: 0,
    averageTimePerSlide: 0,
    mostViewedSlide: null,
    leastViewedSlide: null
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const [viewerInfo, setViewerInfo] = useState(null);
  const [viewerFormOpen, setViewerFormOpen] = useState(true);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    fullName: ''
  });

  // Analytics tracking refs
  const sessionStartTime = useRef(Date.now());
  const pdfViewStartTime = useRef(null);
  const currentSlideStartTime = useRef(null);
  const currentSlideNumber = useRef(null);
  const slideViewSequence = useRef(0);
  const iframeRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    const checkAdminStatus = async () => {
      // Check if user is the global admin
      if (user?.uid === "gJmQhlbcSza0nm0HLx58L8os8wU2") {
        setIsAdmin(true);
        return;
      }

      // If user is authenticated and we have a companyId, check if they're the company owner
      if (user && companyId) {
        try {
          const companyDoc = await getDoc(doc(db, 'companies', companyId));
          if (companyDoc.exists() && companyDoc.data().userId === user.uid) {
            setIsAdmin(true);
          }
        } catch (error) {
          console.error('Error checking company ownership:', error);
        }
      }
    };

    checkAdminStatus();
  }, [companyId]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        // 1. Lookup handle mapping (handles are stored lowercase in Firestore)
        const normalizedHandle = (handle || '').toLowerCase().trim();
        const handleDoc = await getDoc(doc(db, 'handles', normalizedHandle));
        if (!handleDoc.exists()) {
          setCompany(null);
          setLoading(false);
          return;
        }
        const { companyId } = handleDoc.data();
        setCompanyId(companyId);
        // 2. Fetch company data
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists()) {
          setCompany(companyDoc.data());
        } else {
          setCompany(null);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [handle]);

  const handlePreview = async () => {
    setPreviewOpen(true);
    setIsFullscreen(true);
    pdfViewStartTime.current = Date.now();
    
    setAnalytics(prev => ({ ...prev, previewCount: prev.previewCount + 1 }));
    await trackAnalytics('pdf_preview_opened', {
      slideCount: company.pitchDeck.slides,
      previewNumber: analytics.previewCount + 1
    });

    // Start with slide 1
    setTimeout(() => {
      handleSlideChange(1);
    }, 1000);
  };

  const handleDocumentPreview = async (document) => {
    // Try to construct a URL if one doesn't exist
    let documentWithUrl = document;
    if (!document.url && document.fileName) {
      // Try to construct a Firebase Storage URL
      const storageRef = ref(storage, `users/demoUser/documents/${document.fileName}`);
      try {
        const url = await getDownloadURL(storageRef);
        documentWithUrl = { ...document, url };
      } catch (error) {
        // URL construction failed, will show fallback modal
      }
    }
    
    setSelectedDocument(documentWithUrl);
    setDocPreviewOpen(true);
    setIsFullscreen(true);
    
    setAnalytics(prev => ({ ...prev, documentPreviewCount: (prev.documentPreviewCount || 0) + 1 }));
    await trackAnalytics('document_preview_opened', {
      documentName: document.name,
      fileName: document.fileName,
      previewNumber: (analytics.documentPreviewCount || 0) + 1
    });
  };

  const handleClosePreview = async () => {
    // Track final slide time
    if (currentSlideStartTime.current && currentSlideNumber.current) {
      const finalSlideTime = Date.now() - currentSlideStartTime.current;
      
      await trackAnalytics('slide_view_complete', {
        slideNumber: currentSlideNumber.current,
        timeSpentMs: finalSlideTime,
        viewSequence: slideViewSequence.current,
        isFinalSlide: true,
        sessionDuration: Date.now() - sessionStartTime.current
      });
    }

    // Track session completion
    if (pdfViewStartTime.current) {
      const totalViewTime = Date.now() - pdfViewStartTime.current;
      
      await trackAnalytics('pdf_preview_session_complete', {
        totalViewTimeMs: totalViewTime,
        slidesViewed: analytics.slidesViewed,
        uniqueSlidesViewed: analytics.uniqueSlidesViewed,
        totalSlideViews: analytics.totalSlideViews,
        slideViewTimes: analytics.slideViewTimes,
        completionRate: Math.round((analytics.uniqueSlidesViewed / (company.pitchDeck.slides || 1)) * 100),
        sessionId: analytics.sessionId
      });
    }
    
    // Reset tracking
    currentSlideStartTime.current = null;
    currentSlideNumber.current = null;
    pdfViewStartTime.current = null;
    
    setPreviewOpen(false);
    setIsFullscreen(false);
  };

  const handleCloseDocumentPreview = async () => {
    await trackAnalytics('document_preview_closed', {
      documentName: selectedDocument?.name,
      fileName: selectedDocument?.fileName,
      sessionDuration: Date.now() - sessionStartTime.current
    });
    
    setDocPreviewOpen(false);
    setSelectedDocument(null);
    setIsFullscreen(false);
  };

  const handleSlideChange = (slideNumber) => {
    // Track previous slide time
    if (currentSlideStartTime.current && currentSlideNumber.current) {
      const slideTime = Date.now() - currentSlideStartTime.current;
      const prevSlide = currentSlideNumber.current;
      
      setAnalytics(prev => ({
        ...prev,
        slideViewTimes: {
          ...prev.slideViewTimes,
          [prevSlide]: (prev.slideViewTimes[prevSlide] || 0) + slideTime
        },
        slideViewDetails: {
          ...prev.slideViewDetails,
          [prevSlide]: [
            ...(prev.slideViewDetails[prevSlide] || []),
            {
              startTime: currentSlideStartTime.current,
              endTime: Date.now(),
              duration: slideTime,
              sequence: slideViewSequence.current
            }
          ]
        }
      }));
    }

    // Start tracking new slide
    currentSlideStartTime.current = Date.now();
    currentSlideNumber.current = slideNumber;
    slideViewSequence.current += 1;

    // Update analytics
    setAnalytics(prev => {
      const newSlidesViewed = prev.slidesViewed.includes(slideNumber) 
        ? prev.slidesViewed 
        : [...prev.slidesViewed, slideNumber];

      return {
        ...prev,
        slidesViewed: newSlidesViewed,
        totalSlideViews: prev.totalSlideViews + 1,
        uniqueSlidesViewed: new Set(newSlidesViewed).size,
        averageTimePerSlide: Object.values(prev.slideViewTimes).reduce((a, b) => a + b, 0) / 
          (Object.keys(prev.slideViewTimes).length || 1)
      };
    });
  };

  const trackAnalytics = async (event, data) => {
    try {
      // Here you would typically send analytics to your backend
      console.log('Analytics event:', event, data);
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const handleAdminView = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Set viewer info without storing in database or analytics
    const viewerData = {
      email: user?.email || 'admin@decklink.co',
      fullName: 'Admin User',
      isAdmin: true
    };

    // Update local state only
    setViewerInfo(viewerData);
    setViewerFormOpen(false);
  };

  const handleViewerFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Basic validation
    if (!formData.email || !formData.fullName) {
      setFormError('Please fill in all fields');
      return;
    }

    if (!formData.email.includes('@')) {
      setFormError('Please enter a valid email address');
      return;
    }

    // Skip analytics for admin users
    if (formData.fullName === 'Admin User') {
      setViewerInfo({
        email: formData.email,
        fullName: formData.fullName,
        isAdmin: true
      });
      setViewerFormOpen(false);
      return;
    }

    try {
      // Store viewer information
      const viewerData = {
        email: formData.email,
        fullName: formData.fullName,
        timestamp: new Date().toISOString(),
        companyId,
        handle
      };

      // Add to viewers collection
      await addDoc(collection(db, 'viewers'), viewerData);

      // Update analytics with viewer info
      setAnalytics(prev => ({
        ...prev,
        viewerInfo: {
          email: formData.email,
          fullName: formData.fullName
        }
      }));

      // Track viewer registration immediately
      await addDoc(collection(db, 'analytics'), {
        companyId,
        eventType: 'viewer_registered',
        timestamp: new Date().toISOString(),
        timestampMs: Date.now(),
        viewerInfo: {
          email: formData.email,
          fullName: formData.fullName
        },
        sessionId: analytics.sessionId,
        sessionDurationMs: Date.now() - sessionStartTime.current,
        sessionStartTime: sessionStartTime.current,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      });

      setViewerInfo(viewerData);
      setViewerFormOpen(false);
    } catch (error) {
      console.error('Error saving viewer information:', error);
      setFormError('Failed to save information. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-gray-600">Company not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Viewer Information Modal */}
      <ViewerForm
        viewerFormOpen={viewerFormOpen}
        formData={formData}
        setFormData={setFormData}
        handleViewerFormSubmit={handleViewerFormSubmit}
        formError={formError}
        company={company}
        isAdmin={isAdmin}
        handleAdminView={handleAdminView}
      />

      {/* Main Content - Only show if viewer info is provided */}
      {!viewerFormOpen && (
        <div className="flex-1 max-w-4xl mx-auto px-2 sm:px-6 py-8 sm:py-12 w-full">
          {/* Company Info & Metrics */}
          <CompanyHeader company={company} />

          {/* Team Section */}
          <TeamSection
            company={company}
            activeTeam={activeTeam}
            setActiveTeam={setActiveTeam}
            selectedMember={selectedMember}
            setSelectedMember={setSelectedMember}
            isBioModalOpen={isBioModalOpen}
            setIsBioModalOpen={setIsBioModalOpen}
          />

          {/* Pitch Deck Section */}
          <PitchDeckSection company={company} handlePreview={handlePreview} />

          {/* Financials Section */}
          <FinancialsSection company={company} />

          {/* Due Diligence Section */}
          <DueDiligenceSection
            company={company}
            activeDoc={activeDoc}
            setActiveDoc={setActiveDoc}
            handleDocumentPreview={handleDocumentPreview}
          />

          {/* Product Demo Section */}
          <ProductDemoSection company={company} />

          {/* Contact Section */}
          <ContactSection company={company} />

          {/* Footer */}
          <Footer />
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewOpen && company.pitchDeck?.url && (
        <PDFPreviewModal
          previewOpen={previewOpen}
          formData={{
            ...company,
            viewerInfo: viewerInfo
          }}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          handleClosePreview={handleClosePreview}
          currentSlideNumber={currentSlideNumber}
          trackAnalytics={trackAnalytics}
          analytics={analytics}
          iframeRef={iframeRef}
        />
      )}

      {/* Document Preview Modal */}
      {docPreviewOpen && selectedDocument && (
        selectedDocument.url ? (
          <PDFPreviewModal
            previewOpen={docPreviewOpen}
            formData={{
              ...company,
              viewerInfo: viewerInfo,
              pitchDeck: {
                url: selectedDocument.url,
                fileName: selectedDocument.fileName,
                slides: 1 // Default for documents
              }
            }}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            handleClosePreview={handleCloseDocumentPreview}
            currentSlideNumber={currentSlideNumber}
            trackAnalytics={trackAnalytics}
            analytics={analytics}
            iframeRef={iframeRef}
          />
        ) : (
          <DocumentPreviewModal
            docPreviewOpen={docPreviewOpen}
            selectedDocument={selectedDocument}
            handleCloseDocumentPreview={handleCloseDocumentPreview}
            company={company}
          />
        )
      )}
    </div>
  );
};

export default LivePreview; 